import { useEffect, useRef } from 'react';

import { useNavigate } from 'react-router-dom';

import { useQueryClient } from '@tanstack/react-query';

import { authClient } from '#lib/authClient';
import { forceSyncCapabilities } from '#lib/authToken';
import { fetcher } from '#lib/backendFetcher';
import { orgInfoOptions } from '#orgFeature/models/org/queries';

// Small delay before retrying /me — covers the brief window where the org membership row exists
// but the JWT org claim has not propagated yet.
const ORG_CLAIM_PROPAGATION_DELAY_MS = 600;

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Reads the registration details the RegisterPage parked for this confirmation
 * (AuthProvider stores them at signup). Null when confirming on another device
 * or for non-signup arrivals (magic link) — the login self-heal covers those.
 * @returns {{email: string, firstName: string, lastName: string, companyName: string}|null}
 */
function readPendingSignup() {
	try {
		const stored = localStorage.getItem('pendingSignup');
		const pending = stored ? JSON.parse(stored) : null;
		return pending?.firstName && pending?.lastName ? pending : null;
	} catch {
		return null;
	}
}

const EmailConfirmationPage = () => {
	const navigate = useNavigate();
	const queryClient = useQueryClient();

	// Run the bootstrap exactly once. Without this guard StrictMode (and any re-render the
	// session refresh triggers) would fire complete-signup and the token refresh multiple times.
	const hasRun = useRef(false);

	useEffect(() => {
		if (hasRun.current) return;
		hasRun.current = true;

		const fetchOrgInfo = () => queryClient.fetchQuery({ ...orgInfoOptions(), retry: false });

		/**
		 * Loads org info with the freshly refreshed token. If the org claim has not propagated to
		 * the JWT yet (/me → 403), wait briefly, refresh the token once more, and retry. Seeding the
		 * cache here means the AuthProvider reads a ready org on mount and never shows its loader.
		 */
		const settleOrgInfo = async () => {
			try {
				return await fetchOrgInfo();
			} catch {
				await delay(ORG_CLAIM_PROPAGATION_DELAY_MS);
				await forceSyncCapabilities();
				return await fetchOrgInfo();
			}
		};

		const bootstrap = async () => {
			try {
				// Give the auth provider a moment to parse the confirmation URL and establish the session.
				await delay(1000);

				// Resolve the session via Better Auth. It verifies the email token on the backend
				// (the link hits /api/auth/verify-email), then the session is available here.
				const { data } = await authClient.getSession();
				const sessionUser = data?.user ?? null;

				// After a successful email confirmation a session MUST exist.
				if (!sessionUser) {
					navigate('/login?error=session_missing');
					return;
				}

				// Only a fresh signup (pending details from this browser) provisions here — invited
				// users and magic-link arrivals already have an org, and cross-device confirmations
				// are handled by the login self-heal (useEnsureOrg), which checks first that the
				// org is genuinely missing.
				const pending = readPendingSignup();
				if (pending) {
					try {
						await fetcher('/api/v1/platform/auth/complete-signup', {
							method: 'POST',
							body: {
								firstName: pending.firstName,
								lastName: pending.lastName,
								companyName: pending.companyName ?? '',
							},
						});
						localStorage.removeItem('pendingSignup');
					} catch {
						navigate('/login?error=profile_creation_failed');
						return;
					}
				}

				// Refresh the JWT so it carries the new org_id / org_role / capability claims.
				await forceSyncCapabilities();

				// complete-signup changes the entire user context — drop all cached server state.
				await queryClient.invalidateQueries();

				// Block until org info is actually fetchable, then navigate. The org query is now
				// warm in the cache, so the AuthProvider renders the app without a loader gap.
				await settleOrgInfo();

				navigate('/dashboard');
			} catch {
				navigate('/login?error=unexpected_error');
			}
		};

		bootstrap();
	}, [navigate, queryClient]);

	return (
		<div className="d-flex justify-content-center align-items-center min-vh-100">
			<div className="text-center">
				<div className="spinner-border text-primary mb-3" role="status">
					<span className="visually-hidden">Wird verarbeitet...</span>
				</div>
				<p>E-Mail wird bestätigt, bitte warten...</p>
			</div>
		</div>
	);
};

export default EmailConfirmationPage;
